import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

let csrfToken = null
let csrfTokenFetchedAt = 0
let csrfFetchPromise = null

// Token CSRF fica em memória + cookie _csrf no browser. Depois de horas com a
// aba aberta (ex.: login parado overnight), o cookie pode expirar/ser removido
// enquanto o valor em memória continua — ensureCsrfToken() achava que ainda
// tinha token válido e não renovava. TTL curto força refresh periódico.
const CSRF_MEMORY_TTL_MS = 30 * 60 * 1000
const CSRF_FETCH_TIMEOUT_MS = 30 * 1000

const CSRF_EXEMPT_SUFFIXES = [
  '/auth/login',
  '/auth/public-register',
  '/auth/forgot-password',
  '/auth/reset-password',
]

function requestPath(url = '') {
  return url.split('?')[0]
}

function isCsrfExempt(url = '') {
  const path = requestPath(url)
  return CSRF_EXEMPT_SUFFIXES.some((suffix) => path.endsWith(suffix))
}

async function fetchCsrfToken(attempt = 0) {
  try {
    // Timeout maior que o padrão: com o backend frio a 1ª resposta passa de 10s.
    const { data } = await apiClient.get('/auth/csrf-token', { timeout: CSRF_FETCH_TIMEOUT_MS })
    csrfToken = data.csrfToken
    csrfTokenFetchedAt = Date.now()
    return csrfToken
  } catch (err) {
    // Aba em background por horas pode falhar na 1ª request após acordar.
    // Timeout não é retentado: repetir só empilha mais espera.
    const timedOut = err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
    if (attempt < 2 && !err.response && !timedOut) {
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)))
      return fetchCsrfToken(attempt + 1)
    }
    throw err
  }
}

export async function ensureCsrfToken(forceRefresh = false) {
  const expired = Date.now() - csrfTokenFetchedAt > CSRF_MEMORY_TTL_MS
  if (!forceRefresh && csrfToken && !expired) {
    return csrfToken
  }

  if (forceRefresh) {
    csrfFetchPromise = null
    csrfToken = null
    csrfTokenFetchedAt = 0
  }

  if (!csrfFetchPromise) {
    csrfFetchPromise = fetchCsrfToken().finally(() => {
      csrfFetchPromise = null
    })
  }

  return csrfFetchPromise
}

export function clearCsrfToken() {
  csrfToken = null
  csrfTokenFetchedAt = 0
  csrfFetchPromise = null
}

function redirectToLoginPage() {
  const path = window.location.pathname || ''
  if (path === '/login' || path.endsWith('/login')) return
  window.location.href = '/login'
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete'])

apiClient.interceptors.request.use(
  async (config) => {
    config.withCredentials = true

    const method = (config.method || 'get').toLowerCase()
    if (MUTATING_METHODS.has(method) && !isCsrfExempt(config.url)) {
      const token = await ensureCsrfToken()
      config.headers['x-csrf-token'] = token
    }

    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // CSRF inválido/expirado: busca novo token e tenta de novo
    if (
      error.response?.status === 403 &&
      !originalRequest._csrfRetry &&
      MUTATING_METHODS.has((originalRequest.method || '').toLowerCase())
    ) {
      originalRequest._csrfRetry = true
      clearCsrfToken()
      const token = await ensureCsrfToken(true)
      originalRequest.headers['x-csrf-token'] = token
      return apiClient(originalRequest)
    }

    const isAuthEndpoint = originalRequest.url?.includes('/auth/')
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true
      try {
        await apiClient.post('/auth/refresh')
        return apiClient(originalRequest)
      } catch (refreshError) {
        redirectToLoginPage()
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default apiClient

// import axios from 'axios'

// const API_BASE_URL = import.meta.env.VITE_API_URL

// const apiClient = axios.create({
//   baseURL: API_BASE_URL,
//   timeout: 10000,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'application/json'
//   }
// })

// apiClient.interceptors.request.use(
//   (config) => {
//     config.withCredentials = true
//     return config
//   },
//   (error) => {
//     return Promise.reject(error)
//   }
// )

// // Interceptor para tratar respostas e erros
// apiClient.interceptors.response.use(
//   (response) => {
//     return response
//   },
//   async (error) => {
//     const originalRequest = error.config

//     // Se o erro é 401, não é endpoint de auth, e não foi tentativa de refresh
//     const isAuthEndpoint = originalRequest.url?.includes('/auth/')
//     if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
//       originalRequest._retry = true

//       try {
//         await apiClient.post('/auth/refresh')
//         return apiClient(originalRequest)
//       } catch (refreshError) {
//         window.location.href = '/login'
//         return Promise.reject(refreshError)
//       }
//     }

//     return Promise.reject(error)
//   }
// )

// export default apiClient